import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import store from "store";
import Api from "../Services/Api";
import AppHeader from "./AppHeader";
import ExperimentsByTrack from "./ExperimentsByTrack";
import GoodPagesSection from "./GoodPagesSection";

const api = Api.create();

const TRACKS = [
  { key: "gsc_fetch",       label: "Pulling 90 days of Search Console data" },
  { key: "baseline",        label: "Analyzing top pages, CTR gaps & page-2 wins" },
  // Standard-tier tracks
  { key: "ctr_fix",         label: "Writing CTR fix experiments" },
  { key: "new_pages",       label: "Proposing new page opportunities" },
  { key: "content_refresh", label: "Identifying content refresh targets" },
  // Thin-tier tracks (small/new sites)
  { key: "foundation",      label: "Running on-page foundation audit" },
  { key: "long_tail",       label: "Capturing long-tail opportunities" },
];

function StatusPill({ state }) {
  const map = {
    done:    { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Done" },
    running: { cls: "bg-indigo-50 text-indigo-700 border-indigo-200",    label: "Running…" },
    failed:  { cls: "bg-red-50 text-red-700 border-red-200",             label: "Failed" },
    pending: { cls: "bg-slate-100 text-slate-500 border-slate-200",      label: "Queued" },
  };
  const v = map[state] || map.pending;
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${v.cls}`}>{v.label}</span>;
}

function fmt(num) {
  if (num == null) return "—";
  if (typeof num !== "number") return String(num);
  if (Math.abs(num) >= 1000) return num.toLocaleString();
  if (num > 0 && num < 1) return `${(num * 100).toFixed(1)}%`;
  return String(num);
}


function matchesFilter(exp, filter) {
  if (!filter || filter === "all") return true;
  if (filter === "priority:high") return exp.priority === "high";
  if (filter.startsWith("track:")) return exp.track === filter.slice("track:".length);
  return true;
}

const FILTER_OPTIONS = [
  { key: "all",             label: "All" },
  { key: "priority:high",   label: "High priority" },
  { key: "track:ctr-fix",          label: "CTR Fix" },
  { key: "track:new-page",         label: "New opportunities" },
  { key: "track:content-refresh",  label: "Content refresh" },
  { key: "track:foundation",       label: "Foundation" },
  { key: "track:long-tail",        label: "Long-tail" },
  { key: "track:protect",          label: "Protect" },
  { key: "track:conversion",       label: "Conversion" },
];

export default function ReportDetailPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const timer = useRef(null);

  useEffect(() => {
    const jwt = store.get("token");
    if (!jwt) {
      navigate("/start", { replace: true });
      return;
    }
    api.setAuthToken(jwt);

    const fetchOnce = async () => {
      const resp = await api.getReport(token);
      if (resp.ok && resp.data?.report) {
        setReport(resp.data.report);
        const progress = resp.data.report.analysis_progress || {};
        const done = ["ctr_fix", "new_pages", "content_refresh"].every(
          (t) => progress[t] === "done" || progress[t] === "failed"
        );
        if (done) {
          if (timer.current) clearInterval(timer.current);
        }
      } else if (resp.status === 404) {
        setError("Report not found");
        if (timer.current) clearInterval(timer.current);
      }
    };

    fetchOnce();
    timer.current = setInterval(fetchOnce, 3500);
    return () => timer.current && clearInterval(timer.current);
  }, [token, navigate]);

  const progress = report?.analysis_progress || {};
  const experiments = report?.experiments || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <button
          onClick={() => navigate("/reports")}
          className="text-sm text-slate-500 hover:text-slate-900 mb-4"
        >
          ← All reports
        </button>

        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">{error}</div>}

        {report && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
              <div>
                <h1 className="text-2xl font-extrabold text-slate-900 break-all">{report.site_url}</h1>
                <p className="text-sm text-slate-500">
                  {report.paid_at ? <>Paid · </> : null}
                  {experiments.length} experiments so far
                  {report.gsc_row_count ? <> · {report.gsc_row_count.toLocaleString()} GSC rows</> : null}
                </p>
              </div>
              <div className="text-right">
                <StatusPill state={report.status === "delivered" ? "done" : "running"} />
              </div>
            </div>

            {/* Progress panel — only shows tracks that actually run for this tier */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Live research progress</div>
              <ul className="space-y-2">
                {TRACKS.filter((t) => {
                  // Always show the two pre-LLM stages.
                  if (t.key === "gsc_fetch" || t.key === "baseline") return true;
                  // Show LLM track rows only if they actually activated
                  // (state or total present in progress).
                  return progress[t.key] != null || progress[`${t.key}_total`] != null;
                }).map((t) => {
                  const total = progress[`${t.key}_total`];
                  const done  = progress[`${t.key}_done`];
                  const showCount = typeof total === "number" && total > 0;
                  return (
                    <li key={t.key} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-slate-700 min-w-0">
                        {t.label}
                        {showCount && (
                          <span className="ml-2 text-xs text-slate-400 tabular-nums">
                            {(done || 0)} / {total}
                          </span>
                        )}
                      </span>
                      <StatusPill state={progress[t.key]} />
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Baseline summary */}
            {report.baseline?.totals && (
              <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Baseline snapshot</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div><div className="text-xs text-slate-500">Pages tracked</div><div className="font-extrabold text-slate-900">{fmt(report.baseline.totals.pages_tracked)}</div></div>
                  <div><div className="text-xs text-slate-500">Queries</div><div className="font-extrabold text-slate-900">{fmt(report.baseline.totals.queries_tracked)}</div></div>
                  <div><div className="text-xs text-slate-500">Total impressions</div><div className="font-extrabold text-slate-900">{fmt(report.baseline.totals.total_impressions)}</div></div>
                  <div><div className="text-xs text-slate-500">Total clicks</div><div className="font-extrabold text-slate-900">{fmt(report.baseline.totals.total_clicks)}</div></div>
                </div>
              </div>
            )}

            {/* Filter chip bar — only render filters that have matching experiments */}
            {experiments.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                {FILTER_OPTIONS.filter((opt) => {
                  if (opt.key === "all") return true;
                  const matches = experiments.filter((e) => matchesFilter(e, opt.key)).length;
                  return matches > 0;
                }).map((opt) => {
                  const count = opt.key === "all"
                    ? experiments.length
                    : experiments.filter((e) => matchesFilter(e, opt.key)).length;
                  const active = filter === opt.key;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => setFilter(opt.key)}
                      className={`px-3 py-1.5 rounded-full border text-xs font-semibold cursor-pointer transition-colors ${
                        active
                          ? "bg-slate-900 border-slate-900 text-white"
                          : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
                      }`}
                    >
                      {opt.label} <span className={active ? "text-slate-300" : "text-slate-400"}>·{" "}{count}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Experiments — grouped by track into digestible sections */}
            <ExperimentsByTrack
              experiments={experiments.filter((e) => matchesFilter(e, filter))}
              isProcessing={report.status !== "delivered"}
              progress={progress}
            />

            {/* Pages Claude marked as already optimized */}
            <GoodPagesSection goodPages={report.good_pages || []} />
          </>
        )}
      </div>
    </div>
  );
}
